import Dexie, { Table } from 'dexie';
import type { Event, Room, Timeslot, Proposal, Session, Vote, ScheduleItem, Mutation } from '../types';

/**
 * Local IndexedDB database for offline-first functionality
 */
class BarcampDatabase extends Dexie {
  events!: Table<Event & { syncedAt?: number }>;
  rooms!: Table<Room & { eventId: string; syncedAt?: number }>;
  timeslots!: Table<Timeslot & { eventId: string; syncedAt?: number }>;
  proposals!: Table<Proposal & { eventId: string; syncedAt?: number }>;
  sessions!: Table<Session & { eventId: string; syncedAt?: number }>;
  votes!: Table<Vote & { eventId: string; syncedAt?: number }>;
  scheduleItems!: Table<ScheduleItem & { eventId: string; syncedAt?: number }>;
  outbox!: Table<Mutation & { id?: number; status: 'pending' | 'sent' | 'failed'; retries: number }>;
  syncState!: Table<{ eventId: string; lastSeq: number; lastSyncAt: number }>;
  userSettings!: Table<{ key: string; value: string }>;

  constructor() {
    super('BarcampBoard');
    
    this.version(1).stores({
      events: 'id, startDate, archivedAt',
      rooms: 'id, eventId, sortOrder',
      timeslots: 'id, eventId, indexInDay',
      proposals: 'id, eventId, createdByNickname, createdAt',
      sessions: 'id, eventId, createdAt',
      votes: 'id, eventId, [targetType+targetId], voterDeviceId',
      scheduleItems: 'id, eventId, sessionId, roomId',
      outbox: '++id, status, clientMutationId',
      syncState: 'eventId',
      userSettings: 'key',
    });
  }
}

export const db = new BarcampDatabase();

/**
 * Generate a unique device ID and store it
 */
export async function getOrCreateDeviceId(): Promise<string> {
  const existing = await db.userSettings.get('deviceId');
  if (existing) {
    return existing.value;
  }
  
  const deviceId = crypto.randomUUID();
  await db.userSettings.put({ key: 'deviceId', value: deviceId });
  return deviceId;
}

/**
 * Get user nickname
 */
export async function getNickname(): Promise<string | null> {
  const setting = await db.userSettings.get('nickname');
  return setting?.value ?? null;
}

/**
 * Set user nickname
 */
export async function setNickname(nickname: string): Promise<void> {
  await db.userSettings.put({ key: 'nickname', value: nickname });
}

/**
 * Get stored access code for an event
 */
export async function getAccessCode(eventId: string): Promise<string | null> {
  const setting = await db.userSettings.get(`accessCode:${eventId}`);
  return setting?.value ?? null;
}

/**
 * Store access code for an event
 */
export async function setAccessCode(eventId: string, code: string): Promise<void> {
  await db.userSettings.put({ key: `accessCode:${eventId}`, value: code });
}

/**
 * Get organizer token for an event
 */
export async function getOrganizerToken(eventId: string): Promise<string | null> {
  const setting = await db.userSettings.get(`organizerToken:${eventId}`);
  return setting?.value ?? null;
}

/**
 * Store organizer token for an event
 */
export async function setOrganizerToken(eventId: string, token: string): Promise<void> {
  await db.userSettings.put({ key: `organizerToken:${eventId}`, value: token });
}

/**
 * Clear organizer token for an event (logout)
 */
export async function clearOrganizerToken(eventId: string): Promise<void> {
  await db.userSettings.delete(`organizerToken:${eventId}`);
}

/**
 * Add a mutation to the outbox for sync
 */
export async function addToOutbox(mutation: Omit<Mutation, 'clientMutationId' | 'clientTs'>): Promise<string> {
  const clientMutationId = crypto.randomUUID();
  await db.outbox.add({
    ...mutation,
    clientMutationId,
    clientTs: new Date().toISOString(),
    status: 'pending',
    retries: 0,
  });
  return clientMutationId;
}

/**
 * Get pending mutations from outbox
 */
export async function getPendingMutations(): Promise<Mutation[]> {
  const items = await db.outbox.where('status').equals('pending').toArray();
  return items.map(({ id: _id, status: _status, retries: _retries, ...mutation }) => mutation);
}

/**
 * Mark mutations as sent
 */
export async function markMutationsSent(clientMutationIds: string[]): Promise<void> {
  await db.outbox.where('clientMutationId').anyOf(clientMutationIds).modify({ status: 'sent' });
}

/**
 * Remove successfully processed mutations
 */
export async function removeMutations(clientMutationIds: string[]): Promise<void> {
  await db.outbox.where('clientMutationId').anyOf(clientMutationIds).delete();
}

/**
 * Get sync state for an event
 */
export async function getSyncState(eventId: string): Promise<{ lastSeq: number; lastSyncAt: number } | null> {
  return await db.syncState.get(eventId) ?? null;
}

/**
 * Update sync state for an event
 */
export async function updateSyncState(eventId: string, lastSeq: number): Promise<void> {
  await db.syncState.put({ eventId, lastSeq, lastSyncAt: Date.now() });
}

/**
 * Override device ID (used when claiming identity from another device via link code)
 */
export async function setDeviceId(deviceId: string): Promise<void> {
  await db.userSettings.put({ key: 'deviceId', value: deviceId });
}

/**
 * Get stored personal link code
 */
export async function getLinkCode(): Promise<string | null> {
  const setting = await db.userSettings.get('linkCode');
  return setting?.value ?? null;
}

/**
 * Store personal link code
 */
export async function setLinkCode(code: string): Promise<void> {
  await db.userSettings.put({ key: 'linkCode', value: code });
}

/**
 * Clear all data for an event (useful when switching events or logging out)
 */
export async function clearEventData(eventId: string): Promise<void> {
  await Promise.all([
    db.rooms.where('eventId').equals(eventId).delete(),
    db.timeslots.where('eventId').equals(eventId).delete(),
    db.proposals.where('eventId').equals(eventId).delete(),
    db.sessions.where('eventId').equals(eventId).delete(),
    db.votes.where('eventId').equals(eventId).delete(),
    db.scheduleItems.where('eventId').equals(eventId).delete(),
    db.syncState.where('eventId').equals(eventId).delete(),
  ]);
}
